import { getTenantInfo } from './getSubdomainPrefix';
import { getRestaurantBySubdomain, ApiError } from '@core/api/restaurant';
import type { Restaurant } from '@shared/types/restaurant';

export type TenantErrorCode =
  | 'NO_SUBDOMAIN'
  | 'INVALID_SUBDOMAIN'
  | 'RESTAURANT_NOT_FOUND'
  | 'RESTAURANT_INACTIVE'
  | 'NETWORK_ERROR'
  | 'UNKNOWN_ERROR';

export interface TenantResolutionResult {
  /** The extracted subdomain prefix, or null if none found */
  subdomainPrefix: string | null;
  /** The resolved restaurant data, or null if not found/error */
  restaurant: Restaurant | null;
  /** The API base URL used for this tenant */
  apiBaseUrl: string | null;
  /** Error message if resolution failed */
  error?: string;
  /** Error code for programmatic handling */
  errorCode?: TenantErrorCode;
}

/**
 * Resolves the current tenant by:
 * 1. Extracting subdomain prefix and API URL from the current URL
 * 2. Fetching restaurant data from the API
 *
 * @returns Resolution result with restaurant data or error information
 */
export async function resolveTenant(): Promise<TenantResolutionResult> {
  const tenantInfo = getTenantInfo();

  // No subdomain found - apex domain or invalid URL
  if (!tenantInfo) {
    return {
      subdomainPrefix: null,
      restaurant: null,
      apiBaseUrl: null,
      error: 'No restaurant specified in URL',
      errorCode: 'NO_SUBDOMAIN',
    };
  }

  const { subdomainPrefix, apiBaseUrl } = tenantInfo;

  try {
    const restaurant = await getRestaurantBySubdomain(subdomainPrefix, apiBaseUrl);

    // Verify restaurant is active
    if (!restaurant.isActive) {
      return {
        subdomainPrefix,
        restaurant: null,
        apiBaseUrl,
        error: 'This restaurant is not currently available',
        errorCode: 'RESTAURANT_INACTIVE',
      };
    }

    // Success!
    return {
      subdomainPrefix,
      restaurant,
      apiBaseUrl,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.isNetworkError()) {
        throw error;
      }
      return {
        subdomainPrefix,
        restaurant: null,
        apiBaseUrl,
        error: error.message,
        errorCode: error.code as TenantErrorCode,
      };
    }

    throw error;
  }
}
